import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';

/**
 * PriorityGradingBar Component
 *
 * A reusable UI component for collecting priority/importance ratings (1-5 scale)
 * with an optional text feedback field. Designed to replace manual text input
 * for priority assessment in the SPAIDR interview system.
 *
 * Features:
 * - Horizontal clickable number bar (1-5) with visual selection feedback
 * - Optional multiline text field for additional feedback
 * - Integrates with existing chat send button (no separate submit button)
 * - Loading state support during submission
 * - Responsive design for mobile and desktop
 * - Enter key submits when priority selected, highlights validation when not
 *
 * @param {Object} props
 * @param {number|null} props.selectedNumber - Currently selected number (1-5)
 * @param {Function} props.onNumberSelect - Callback when a number is selected
 * @param {string} props.feedbackText - Optional feedback text
 * @param {Function} props.onFeedbackChange - Callback when feedback text changes
 * @param {Function} props.onSubmit - Callback when Enter is pressed (if priority selected)
 * @param {boolean} props.disabled - Whether the component is disabled
 */
const PriorityGradingBar = ({
  selectedNumber,
  onNumberSelect,
  feedbackText,
  onFeedbackChange,
  onSubmit,
  disabled = false
}) => {
  const numbers = [1, 2, 3, 4, 5];
  const [showValidation, setShowValidation] = useState(false);
  const boxRef = useRef(null);
  const textFieldRef = useRef(null);

  // Auto-focus the component when it mounts for keyboard interaction
  useEffect(() => {
    if (boxRef.current) {
      boxRef.current.focus();
    }
  }, []);

  const handleBoxKeyDown = (e) => {
    // Only handle number keys at box level
    if (['1', '2', '3', '4', '5'].includes(e.key)) {
      e.preventDefault();
      e.stopPropagation();
      const num = parseInt(e.key, 10);
      onNumberSelect(num);

      // Auto-focus text field after number selection for seamless typing
      setTimeout(() => {
        if (textFieldRef.current) {
          textFieldRef.current.focus();
        }
      }, 50);
    }
  };

  const handleTextFieldKeyDown = (e) => {
    // Handle number keys in text field
    if (['1', '2', '3', '4', '5'].includes(e.key)) {
      e.preventDefault();
      e.stopPropagation();
      const num = parseInt(e.key, 10);
      onNumberSelect(num);
      return;
    }

    // Handle Enter key - always submit if priority selected
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();

      if (selectedNumber !== null) {
        // Priority selected - submit
        if (onSubmit) {
          onSubmit();
        }
      } else {
        // No priority selected - show validation
        setShowValidation(true);
        setTimeout(() => setShowValidation(false), 2000);
      }
    }
  };

  return (
    <Box
      ref={boxRef}
      onKeyDown={handleBoxKeyDown}
      tabIndex={0}
      sx={{
        width: '100%',
        p: 1.5,
        backgroundColor: '#f5f5f5',
        borderRadius: '12px',
        border: '1px solid #e0e0e0',
        '&:focus': {
          outline: 'none'
        }
      }}
    >
      {/* Number Selection Bar with Low/High Labels */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={{
          display: 'flex',
          gap: { xs: 1, sm: 1.5 },
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: { xs: 'wrap', sm: 'nowrap' }
        }}>
          {/* Low label */}
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.75rem',
              color: '#999',
              fontWeight: 500,
              minWidth: '30px',
              textAlign: 'right'
            }}
          >
            Low
          </Typography>

          {/* Number buttons */}
          {numbers.map((num) => (
            <Button
              key={num}
              variant={selectedNumber === num ? 'contained' : 'outlined'}
              onClick={() => onNumberSelect(num)}
              disabled={disabled}
              sx={{
                flex: { xs: '0 0 calc(20% - 8px)', sm: 1 },
                minWidth: { xs: '45px', sm: '50px' },
                height: { xs: '45px', sm: '50px' },
                fontSize: { xs: '1.1rem', sm: '1.3rem' },
                fontWeight: 'bold',
                borderRadius: '10px',
                border: selectedNumber === num ? 'none' : showValidation ? '3px solid #d32f2f' : '2px solid #ccc',
                backgroundColor: selectedNumber === num ? '#011c2d' : 'white',
                color: selectedNumber === num ? 'white' : '#333',
                transition: 'all 0.2s ease-in-out',
                boxShadow: selectedNumber === num ? '0 3px 6px rgba(1, 28, 45, 0.3)' : showValidation ? '0 0 8px rgba(211, 47, 47, 0.4)' : 'none',
                transform: selectedNumber === num ? 'scale(1.05)' : 'scale(1)',
                '&:hover': {
                  backgroundColor: selectedNumber === num ? '#023a5a' : '#011c2d',
                  color: 'white',
                  borderColor: selectedNumber === num ? '#023a5a' : '#011c2d',
                  transform: 'scale(1.05)',
                  boxShadow: '0 3px 6px rgba(1, 28, 45, 0.4)'
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#999',
                  borderColor: '#ccc'
                }
              }}
            >
              {num}
            </Button>
          ))}

          {/* High label */}
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.75rem',
              color: '#999',
              fontWeight: 500,
              minWidth: '30px',
              textAlign: 'left'
            }}
          >
            High
          </Typography>
        </Box>
      </Box>

      {/* Optional Feedback Text Field - removed label, only placeholder */}
      <TextField
        inputRef={textFieldRef}
        fullWidth
        multiline
        rows={1}
        value={feedbackText}
        onChange={(e) => onFeedbackChange(e.target.value)}
        onKeyDown={handleTextFieldKeyDown}
        disabled={disabled}
        placeholder="Optional: Add context or reasoning for your rating..."
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            backgroundColor: 'white',
            fontSize: '0.85rem',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#011c2d',
              borderWidth: '2px'
            }
          }
        }}
      />
    </Box>
  );
};

export default PriorityGradingBar;
